import {
  EventHandler,
  MouseEventHandler,
  useCallback,
  useState,
  useMemo,
  ChangeEvent,
  MouseEvent,
  useRef,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { Formik, FormikHelpers } from 'formik';
import { TextField, Box, Button, Card } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { serializeId } from 'utils';
import cn from 'classnames';
import { KEYBOARD_KEYS } from '@constants';

type ValueOfField = string | boolean;

export type TodoListField = {
  type?: string; // default: text
  name: string; // must be unique
  label: string; //
  value: ValueOfField;
  placeholder?: string;
  isEditable?: boolean;
};

type Handlers = {
  handleBlur: (e: React.FocusEvent<any>) => void;
  handleChange: (e: ChangeEvent<any>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement> | undefined) => void;
  handleAddField: ({
    event,
    values,
  }: {
    event: MouseEvent<any>;
    values: Record<string, ValueOfField>;
  }) => void;
  handleDelete: (field: TodoListField) => void;
  handleEditLabel: ({ field, newLabel }: { field: TodoListField; newLabel: string }) => void;
  handleUpdateField: ({
    fieldToUpdate,
    values,
  }: {
    fieldToUpdate: TodoListField;
    values: Record<string, ValueOfField>;
  }) => void;
  handleToggleFieldEditable: ({ field }: { field: TodoListField }) => void;
  toggleEditingLabelStates: (field: TodoListField) => void;
};

type HookProps = {
  fields: TodoListField[];
  formId: string; // Mandatory in case this hook is used several places
  onSubmit?: ({
    fields,
    values,
    actions,
  }: {
    fields: TodoListField[];
    values: Record<string, ValueOfField>;
    actions: FormikHelpers<any>;
  }) => void;
  renderFormBody?: ({
    handleBlur,
    handleChange,
    handleSubmit,
    handleAddField,
    values,
    formFields,
  }: {
    handleBlur: Handlers['handleBlur'];
    handleChange: Handlers['handleChange'];
    handleSubmit: Handlers['handleSubmit'];
    handleAddField: Handlers['handleAddField'];
    handleDelete: Handlers['handleDelete'];
    handleEditLabel: Handlers['handleEditLabel'];
    handleToggleFieldEditable: Handlers['handleToggleFieldEditable'];
    values: Record<string, ValueOfField>;
    formFields: TodoListField[];
  }) => JSX.Element;
};

const makeFormValues = (fields: TodoListField[] = []) => {
  return (fields ?? []).reduce((acc: Record<string, ValueOfField>, field) => {
    acc[field.name] = field.value;
    return acc;
  }, {});
};

const defaultLabel = 'Click to edit note header';

const useSimpleTodoList = ({ fields, formId, onSubmit, renderFormBody }: HookProps) => {
  const lastFieldsRef = useRef(fields);
  const [formFields, setFormFields] = useState(fields || []);
  const [editingStates, setEditingStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!fields || fields === lastFieldsRef.current) return;

    lastFieldsRef.current = fields;

    setFormFields(fields);
  }, [fields]);

  const formValues = useMemo(() => makeFormValues(formFields), [formFields]);

  const toggleEditingLabelStates = useCallback((field: TodoListField) => {
    const labelKey = serializeId(['label', field.name]);

    setEditingStates((prev) => ({
      ...prev,
      [labelKey]: !prev[labelKey],
    }));
  }, []);

  const handleUpdateField = useCallback(
    ({
      fieldToUpdate,
      values,
    }: {
      fieldToUpdate: TodoListField;
      values: Record<string, ValueOfField>;
    }) => {
      const nextFieldsState = formFields.map((field) =>
        fieldToUpdate.name === field.name
          ? {
              ...field,
              value: values[field.name],
            }
          : field,
      );

      setFormFields(nextFieldsState);
    },
    [formFields],
  );

  const handleFormSubmit = useCallback(
    (values: Record<string, ValueOfField>, actions: FormikHelpers<any>) => {
      const nextFieldsState = formFields.map((oldField) => ({
        ...oldField,
        value: values[oldField.name],
      }));
      setFormFields(nextFieldsState);

      if (onSubmit) {
        onSubmit({ fields: nextFieldsState, values, actions });
      }
    },
    [formFields, onSubmit],
  );

  const handleAddField = useCallback(
    ({ values }: { event: MouseEvent<Element>; values: Record<string, ValueOfField> }) => {
      const uuid = uuidv4();
      const newField = {
        type: 'text',
        name: uuid,
        label: defaultLabel,
        value: '',
        placeholder: 'Enter note',
        isEditable: false,
      };
      const nextFieldsState = formFields.map((oldField) => ({
        ...oldField,
        value: values[oldField.name],
      }));
      nextFieldsState.push(newField);

      setFormFields(nextFieldsState);
    },
    [formFields],
  );

  const handleDelete = useCallback((fieldToDelete: TodoListField) => {
    setFormFields((prev) => prev.filter((field) => field.name !== fieldToDelete.name));
  }, []);

  const handleEditLabel = useCallback(
    ({ field, newLabel }: { field: TodoListField; newLabel: string }) => {
      if (!newLabel?.trim()) {
        toggleEditingLabelStates(field);
        return;
      }
      const nextFieldsState = formFields.map((currField) =>
        currField.name === field.name ? { ...currField, label: newLabel } : currField,
      );

      setFormFields(nextFieldsState);
      toggleEditingLabelStates(field);
    },
    [formFields, toggleEditingLabelStates],
  );

  const handleToggleFieldEditable = useCallback(
    ({ field }: { field: TodoListField }) => {
      const nextFieldsState = formFields.map((currField) =>
        currField.name === field.name
          ? { ...currField, isEditable: !currField.isEditable }
          : currField,
      );
      setFormFields(nextFieldsState);
    },
    [formFields],
  );

  const List = (
    <Formik initialValues={formValues} enableReinitialize onSubmit={handleFormSubmit}>
      {({ handleBlur, handleChange, handleSubmit, values, isSubmitting }) =>
        renderFormBody ? (
          renderFormBody({
            handleBlur,
            handleChange,
            handleSubmit,
            handleAddField,
            handleDelete,
            handleEditLabel,
            handleToggleFieldEditable,
            values,
            formFields,
          })
        ) : (
          <Box>
            <Box
              component="form"
              sx={{ height: 450, overflow: 'auto' }}
              id={formId}
              onSubmit={isSubmitting ? undefined : handleSubmit}
            >
              {formFields.map((field) => (
                <DefaulInput
                  key={field.name}
                  field={field}
                  values={values}
                  toggleEditingLabelStates={toggleEditingLabelStates}
                  editingStates={editingStates}
                  handleDelete={handleDelete}
                  handleChange={handleChange}
                  handleBlur={(e: any) => {
                    handleUpdateField({ fieldToUpdate: field, values });
                    handleBlur(e);
                    e.preventDefault();
                  }}
                  handleEditLabel={handleEditLabel}
                  handleUpdateField={handleUpdateField}
                  handleToggleFieldEditable={handleToggleFieldEditable}
                />
              ))}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '5px',
                justifyContent: 'space-between',
                my: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={(e) => handleAddField({ event: e, values })}
                disabled={isSubmitting}
              >
                +
              </Button>
              <Button
                color="primary"
                variant="contained"
                form={formId}
                type="submit"
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </Box>
          </Box>
        )
      }
    </Formik>
  );

  return {
    List,
    formValues,
  };
};

export default useSimpleTodoList;

const StyledInputContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  margin: 2rem 0;
  gap: 15px;
  padding: 15px;

  .header {
    cursor: pointer;
    &.notEditable {
      cursor: not-allowed;
      color: ${(props) => props.theme.palette.text.disabled};
    }
  }
`;

const DefaulInput = ({
  field,
  values,
  toggleEditingLabelStates,
  editingStates,
  handleDelete,
  handleChange,
  handleBlur,
  handleEditLabel,
  handleUpdateField,
  handleToggleFieldEditable,
}: {
  field: TodoListField;
  values: Record<string, ValueOfField>;
  editingStates: Record<string, boolean>;
  toggleEditingLabelStates: Handlers['toggleEditingLabelStates'];
  handleUpdateField: Handlers['handleUpdateField'];
  handleBlur: Handlers['handleBlur'];
  handleChange: Handlers['handleChange'];
  handleDelete: Handlers['handleDelete'];
  handleEditLabel: Handlers['handleEditLabel'];
  handleToggleFieldEditable: Handlers['handleToggleFieldEditable'];
}) => {
  const labelRef = useRef(document.createElement('input'));

  const isEditingLabel = editingStates[serializeId(['label', field.name])];

  return (
    <StyledInputContainer variant="outlined" key={field.name} raised>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'contents',
          }}
        >
          {!isEditingLabel ? (
            <Box
              className={cn('header', {
                notEditable: !field.isEditable,
              })}
              onClick={field.isEditable ? () => toggleEditingLabelStates(field) : undefined}
              sx={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {field.label}
            </Box>
          ) : (
            <TextField
              autoFocus
              onKeyPress={(e) => {
                if (e.key === KEYBOARD_KEYS.ENTER) {
                  handleEditLabel({ field, newLabel: labelRef.current.value });
                }
              }}
              onBlur={() => handleEditLabel({ field, newLabel: labelRef.current.value })}
              sx={{ pr: 1.25 }}
              fullWidth
              inputRef={labelRef}
              defaultValue={field.label === defaultLabel ? '' : field.label}
            />
          )}
        </Box>

        <Box
          sx={{
            gap: '5px',
            display: 'flex',
          }}
        >
          <Button
            sx={{
              width: 'fit-content',
              bgcolor: 'grey.500',
              color: 'white',
            }}
            variant="contained"
            onClick={() => handleToggleFieldEditable({ field })}
          >
            {field.isEditable ? 'Lock' : 'Edit'}
          </Button>
          {field.isEditable && (
            <Button
              sx={{
                width: 'fit-content',
                bgcolor: 'error.main',
                color: 'white',
              }}
              variant="contained"
              onClick={() => handleDelete(field)}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>
      <TextField
        type={field.type || 'text'}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={(e) => {
          if (e.key === KEYBOARD_KEYS.ENTER) {
            e.preventDefault();
            handleUpdateField({ fieldToUpdate: field, values });
            (e.target as HTMLElement).blur();
          }
        }}
        value={values[field.name]}
        name={field.name}
        placeholder={field.placeholder || ''}
        variant="outlined"
        required
        disabled={field.isEditable === false}
      />
    </StyledInputContainer>
  );
};
